import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toast } from '@/shared/lib/toast'
import {
  type AddAddonRequest,
  contractService,
} from '../../services/contractService'

interface AddonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contractId?: string
  existingAddonIds?: string[]
  onAddAddon?: (addonPlanId: string) => void
}

export function AddonDialog({
  open,
  onOpenChange,
  contractId,
  existingAddonIds = [],
  onAddAddon,
}: AddonDialogProps) {
  const [selectedAddon, setSelectedAddon] = useState<string>('')

  const queryClient = useQueryClient()

  // Fetch available addon plans
  const { data: availableAddons = [], isLoading } = useQuery({
    queryKey: ['addon-plans'],
    queryFn: contractService.getAvailableAddons,
    enabled: open,
  })

  // Add addon mutation (only for edit mode when contractId exists)
  const addAddonMutation = useMutation({
    mutationFn: (addonData: AddAddonRequest) =>
      contractService.addAddon(contractId!, addonData),
    onSuccess: () => {
      toast.success('Success', 'Addon added to contract successfully')
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] })
      queryClient.invalidateQueries({
        queryKey: ['contract-addons', contractId],
      })
      onOpenChange(false)
      // Reset form
      setSelectedAddon('')
    },
    onError: (error: Error) => {
      toast.error('Error', error.message || 'Failed to add addon')
    },
  })

  const handleAddAddon = () => {
    if (!selectedAddon) return

    const selectedAddonPlan = availableAddons.find(
      addon => addon.id === selectedAddon
    )
    if (!selectedAddonPlan) return

    if (contractId) {
      // Edit mode: Add to existing contract via API
      const addonData: AddAddonRequest = {
        plan_id: selectedAddon,
        quantity: 1,
      }

      addAddonMutation.mutate(addonData)
    } else {
      // Create mode: Just pass the plan ID to the parent
      onAddAddon?.(selectedAddon)
      onOpenChange(false)

      // Reset form
      setSelectedAddon('')
    }
  }

  const availableOptions = availableAddons.filter(
    addon => !existingAddonIds.includes(addon.id)
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[80vh]'>
        <DialogHeader>
          <DialogTitle>Add Addon</DialogTitle>
          <p className='text-sm text-muted-foreground'>
            Select an addon to add to this contract
          </p>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>Select Addon Plan</Label>
            <Select
              value={selectedAddon}
              onValueChange={setSelectedAddon}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoading ? 'Loading addons...' : 'Choose an addon plan...'
                  }
                >
                  {selectedAddon &&
                    availableAddons.find(a => a.id === selectedAddon)?.name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableOptions.map(addon => (
                  <SelectItem key={addon.id} value={addon.id}>
                    <div className='flex flex-col'>
                      <span className='font-medium'>{addon.name}</span>
                      {addon.description && (
                        <span className='text-sm text-muted-foreground'>
                          {addon.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex justify-end gap-3'>
            <Button
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={contractId ? addAddonMutation.isPending : false}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAddon}
              disabled={
                !selectedAddon ||
                (contractId ? addAddonMutation.isPending : false)
              }
            >
              {contractId && addAddonMutation.isPending
                ? 'Adding...'
                : 'Add Addon'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
